/**
 * Voice estimator seat — Agents as Users Phase 2 (AGENTS-AS-USERS-PRD §4/§7).
 * Registry: voice_estimator_valuation — participant standing. Transport:
 * buildInstructionStack (VOICE_ESTIMATOR_CARD) + callFeatureLlm.
 *
 * The ONLY writer of counted response snapshots. Invoked exclusively for
 * seats with seat_type = 'estimator' AND counting_policy = 'counted'
 * (facilitator opt-in, migration 601). Estimates join official aggregation,
 * always badged; the aggregate still flows through human acceptance and
 * ratification — voice, never authority.
 */

import { callFeatureLlm } from '../../lib/llmClient';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { buildInstructionStack } from '../../lib/ai/instructionStack';
import { VOICE_ESTIMATOR_CARD } from '../../lib/ai/agentCard';
import type { ToolResultItem } from '../../types/prioritization/toolSession';
import { formatCorpusForPrompt, loadVoiceCorpusForPrompt, voiceCorpusLabel } from './voiceCorpusService';
import { parseJsonObject, type VoiceValuationItemInput } from './voiceAdvisoryService';
import type { SessionVoiceSeat, VoiceCorpusDocument, WorkspaceVoice } from './types';

export interface VoiceEstimateItem {
  itemId: string;
  estimate: number;
  rationale: string;
}

export interface GenerateVoiceEstimatesInput {
  voice: WorkspaceVoice;
  items: VoiceValuationItemInput[];
  /** Preloaded corpus; loaded from the Voice's documents when omitted. */
  corpus?: VoiceCorpusDocument[];
  criteriaSummary?: string;
  /** Stable conversation scoping (usually the tool session id). */
  conversationKey?: string;
}

export interface GenerateVoiceEstimatesResult {
  summary: string;
  items: VoiceEstimateItem[];
  error: string | null;
}

/**
 * Parse the model's JSON into estimate rows. Defensive: tolerates fences,
 * string numbers, and junk rows; drops anything without a finite estimate.
 */
export function parseVoiceEstimates(raw: string): {
  summary: string | null;
  items: VoiceEstimateItem[];
} {
  const parsed = parseJsonObject(raw);
  if (!parsed) return { summary: null, items: [] };
  const rawItems = Array.isArray(parsed.items) ? parsed.items : [];
  const items = (rawItems as unknown[])
    .map((r) => {
      const row = r as Record<string, unknown>;
      const itemId = String(row.itemId ?? row.item_id ?? '').trim();
      const estimateRaw = row.estimate ?? row.score ?? row.value;
      if (estimateRaw == null) return null;
      const estimate =
        typeof estimateRaw === 'number' ? estimateRaw : Number(String(estimateRaw).trim());
      if (!itemId || !Number.isFinite(estimate)) return null;
      return {
        itemId,
        estimate,
        rationale: String(row.rationale ?? row.commentary ?? '').trim().slice(0, 400),
      };
    })
    .filter((r): r is VoiceEstimateItem => r != null);
  const summary = typeof parsed.summary === 'string' ? parsed.summary : null;
  return { summary, items };
}

/** Generate per-item numeric estimates + one-line rationales for an estimator Voice. */
export async function generateVoiceEstimates(
  input: GenerateVoiceEstimatesInput,
): Promise<GenerateVoiceEstimatesResult> {
  const docs = input.corpus ?? (await loadVoiceCorpusForPrompt(input.voice.id));
  const corpusBlock = formatCorpusForPrompt(docs);
  const corpusLabel = voiceCorpusLabel(input.voice);

  const { systemPrompt } = buildInstructionStack({
    card: VOICE_ESTIMATOR_CARD,
    capabilityAppendix: `You are estimating as "${input.voice.displayName}", grounded in your ${corpusLabel}.
Estimate each item's relative priority value on a 0-100 scale (100 = highest value to your constituency).
Every estimate needs a one-line rationale citing the corpus signal behind it.
Cover every item. Return ONLY the JSON object from your output contract.`,
  });

  const itemList = input.items
    .map((it, i) => `${i + 1}. id=${it.id} | ${it.title}${it.description ? ` — ${it.description.slice(0, 200)}` : ''}`)
    .join('\n');

  const userPrompt = `Corpus (${corpusLabel}):
${corpusBlock}

${input.criteriaSummary ? `Session criteria context:\n${input.criteriaSummary}\n` : ''}
Items to estimate (0-100 each):
${itemList}`;

  const content = await callFeatureLlm({
    conversationId: `voice-estimate:${input.voice.id}:${input.conversationKey ?? 'adhoc'}`,
    systemPrompt,
    userPrompt,
    temperature: 0.2,
    maxTokens: 2500,
  });

  if (!content) {
    return { summary: '', items: [], error: 'Estimator model unavailable — no estimates generated.' };
  }

  const { summary, items } = parseVoiceEstimates(content);
  if (items.length === 0) {
    return { summary: '', items: [], error: 'Estimator returned no parseable estimates.' };
  }

  // Only estimates for known items; no invented rows.
  const knownIds = new Set(input.items.map((it) => it.id));
  const filtered = items.filter((it) => knownIds.has(it.itemId));

  return {
    summary: summary ?? `${input.voice.displayName} estimator pass over ${filtered.length} items.`,
    items: filtered,
    error: null,
  };
}

function estimatesToToolResults(
  items: VoiceEstimateItem[],
  itemTitles?: Record<string, string>,
): ToolResultItem[] {
  return items.map((r) => ({
    id: r.itemId,
    name: itemTitles?.[r.itemId] ?? r.itemId,
    score: r.estimate,
    metadata: {
      kind: 'voice_estimate',
      rationale: r.rationale,
    },
  }));
}

export interface RunVoiceEstimateInput {
  voice: WorkspaceVoice;
  seat: SessionVoiceSeat;
  toolSessionId: string;
  workspaceId: string;
  items: VoiceValuationItemInput[];
  facilitatorId?: string | null;
  criteriaSummary?: string;
}

/**
 * Generate + persist a counted estimator response. Hard-gated on the seat:
 * seat_type must be 'estimator' AND counting_policy must be 'counted'
 * (facilitator opt-in) — any other seat is refused, not downgraded.
 */
export async function runVoiceEstimate(
  input: RunVoiceEstimateInput,
): Promise<{ summary: string; items: VoiceEstimateItem[]; error: string | null }> {
  if (input.seat.seatType !== 'estimator' || input.seat.countingPolicy !== 'counted') {
    return {
      summary: '',
      items: [],
      error:
        'Estimates require an estimator seat with facilitator-opted-in counted policy (PRD §4).',
    };
  }

  const generated = await generateVoiceEstimates({
    voice: input.voice,
    items: input.items,
    criteriaSummary: input.criteriaSummary,
    conversationKey: input.toolSessionId,
  });
  if (generated.error || generated.items.length === 0) {
    return { summary: generated.summary, items: [], error: generated.error ?? 'No estimates.' };
  }

  const itemTitles = Object.fromEntries(input.items.map((it) => [it.id, it.title]));

  try {
    const supabase = getSupabaseClient();
    const row = {
      tool_session_id: input.toolSessionId,
      workspace_id: input.workspaceId,
      participant_id: input.voice.id,
      participant_name: input.voice.displayName,
      results: estimatesToToolResults(generated.items, itemTitles),
      is_complete: true,
      submitted_at: new Date().toISOString(),
      principal_type: 'voice',
      voice_id: input.voice.id,
      sponsor_user_id: input.voice.sponsorUserId,
      facilitator_id: input.facilitatorId ?? null,
      rationale: generated.summary,
      // Migration 601: the audit-stable counted snapshot. This service is the
      // only writer of counted rows.
      counting_policy: 'counted',
      creation_source: 'ai',
      creation_review_status: 'none',
      creation_provenance: 'ai_draft',
    };
    const { error } = await supabase
      .from('tool_session_responses')
      .upsert(row, { onConflict: 'tool_session_id,participant_id' });
    return { summary: generated.summary, items: generated.items, error: error?.message ?? null };
  } catch (err) {
    return {
      summary: generated.summary,
      items: generated.items,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}
